/**
 * The confirmation a past-dated save raises (MDOT-25046, AC-2).
 *
 * The day is picked in the editor's header and the save is pressed somewhere else entirely, so the
 * button that writes it says nothing about the date it carries. This asks at that moment, and asks
 * for every save that carries one — the portfolio's own composition and a proposal's alike, since
 * `editDate` reaches both.
 *
 * It performs nothing: cancelling leaves the composition, the date and the URL as they were.
 */
import type { MinimumDialogProps } from "$root/components/spawnable/type";
import { useLocaleFormatters } from "$root/localization/hooks";
import { Dialog, DialogFooter, Text } from "@mdotm/shadcn/legacy/components";
import { Button, SubmitButton } from "@mdotm/shadcn/mdotm/components";
import { type SpawnResult, adaptAnimatedNodeProvider, spawn } from "@mdotm/shadcn/legacy/react-extensions";
import { useTranslation } from "react-i18next";
import { pastDatedEditDate } from "./marketDate";

type PastDateConfirmDialogProps = {
	/** The day the save would be dated to (`YYYY-MM-DD`, the user's local calendar). */
	date: string;
	onConfirm(): void;
};

function PastDateConfirmDialog({
	show,
	onClose,
	onAnimationStateChange,
	date,
	onConfirm,
}: PastDateConfirmDialogProps & MinimumDialogProps) {
	const { t } = useTranslation();
	const { formatDate } = useLocaleFormatters();

	return (
		<Dialog
			show={show}
			onClose={onClose}
			header={t("EDIT_COMPOSITION.REFERENCE_DATE.PAST_SAVE_CONFIRM.TITLE")}
			onAnimationStateChange={onAnimationStateChange}
			footer={
				<DialogFooter
					neutralAction={
						<Button palette="tertiary" onClick={onClose} data-qualifier="CompositionEditor/PastDateSave/Cancel">
							{t("EDIT_COMPOSITION.REFERENCE_DATE.PAST_SAVE_CONFIRM.CANCEL")}
						</Button>
					}
					primaryAction={
						<SubmitButton data-qualifier="CompositionEditor/PastDateSave/Confirm">
							{t("EDIT_COMPOSITION.REFERENCE_DATE.PAST_SAVE_CONFIRM.CONFIRM")}
						</SubmitButton>
					}
				/>
			}
			onSubmitAsync={() => {
				onConfirm();
			}}
		>
			<Text type="Body/M/Book" as="p" data-qualifier="CompositionEditor/PastDateSave/Body">
				{t("EDIT_COMPOSITION.REFERENCE_DATE.PAST_SAVE_CONFIRM.BODY", { date: formatDate(date) })}
			</Text>
		</Dialog>
	);
}

/** Resolves `true` when the user confirms the past-dated save, `false` on cancel or dismissal. */
export function spawnPastDateConfirmDialog(props: Pick<PastDateConfirmDialogProps, "date">): SpawnResult<boolean> {
	return spawn<boolean>(
		adaptAnimatedNodeProvider(({ show, resolve, onHidden }) => (
			<PastDateConfirmDialog
				{...props}
				show={show}
				onAnimationStateChange={(state) => state === "hidden" && onHidden()}
				onClose={() => resolve(false)}
				onConfirm={() => resolve(true)}
			/>
		)),
	);
}

/**
 * Whether the save may go ahead: `true` outright at a day that dates nothing.
 *
 * {@link pastDatedEditDate} decides which day that is, so the question the dialog asks and the
 * `editDate` the request carries are the same test — a save that asks is a save that dates.
 */
export async function confirmPastDatedSave(day: string | null | undefined): Promise<boolean> {
	if (!day || pastDatedEditDate(day) === undefined) {
		return true;
	}
	return await spawnPastDateConfirmDialog({ date: day }).promise;
}
